import JsonDump from "./helpers/JsonDump.vue";
import type {App, UnwrapNestedRefs} from "vue";
import {nextTick, computed} from "vue";
import {createApp, isReactive, reactive} from "vue";
import type {EventBus, FormEnvironment} from "./Types";
import {provideEventBus} from "./Environment";
import usePreview from "./helpers/Preview";
import type {ErrorMap} from "./PartInjectKeys";
import {contextKey} from "./PartInjectKeys";
import DuplicateParties from "./helpers/DuplicateParties.vue";
import {matchError} from "./Part";
import type {ApiError} from "@t/util/ApiError";
import {AppContextMap, ComputedContext, contextBuilder} from "@t/fd/AppContext";
import {dialogControls, FormDefinition} from "@t/fd/layout/FormDefinition";

import { EaType } from "@t/customer/EAddress";
import { SrcType } from "@t/meta/SourceType";
import {DialogDisplay, FormDialogDisplay} from "@t/ui/Dialog.ts";
import type {FormPartOrReference} from "@t/fd/FormPartOrReference.ts";
import {FormData} from "@t/fd/FormPart.ts";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import UiRoot from "@fd/containers/UiRoot.vue";
import CellClick from "@fd/containers/agenda/CellClick.vue";
import RowObjectCreate from "@/dialog/RowObjectCreate.vue";


function installDirectives(app:App) {
    app.directive("preview", usePreview());

	// Support agenda/items rendering
	app.component('cell-click', CellClick);
}


export interface InstalledApp {
    app: App,
    data: UnwrapNestedRefs<FormData>,
    errors: UnwrapNestedRefs<ErrorMap>,
    bus: EventBus,
    clearErrors: (path?:string) => void,
    addError: (error: ApiError) => void,
    setDirty: (dirtyFlag:boolean) => void,
    setReadOnly: (dirtyFlag:boolean|null) => void,
    setViewMode: (dirtyFlag:boolean|null) => void,
    replaceData: (data:FormData, clearDirty?:boolean) => void,
    buildContext: ComputedContext
}



const DEFAULT_DIPLAY:FormDialogDisplay = { mode: "form" };
const DEFAULT_ACTIONS:FormPartOrReference[] = ['save', 'saveAndBack'];

function installForm(container:string|Element, env:FormEnvironment, root:InterfaceRoot<FormDefinition>, data:FormData,
                     readonly = false, viewmode = false, context?:AppContextMap,
					 display:DialogDisplay = DEFAULT_DIPLAY,
					 actions:FormPartOrReference[] = DEFAULT_ACTIONS):InstalledApp {

	const rd = isReactive(data) ? data : reactive<FormData>(data);
    const errors:ErrorMap = reactive<ErrorMap>([]);
    const app = createApp(UiRoot, {
        part: root,
        data: rd,
        readonly: readonly,
        viewmode: viewmode,
		display: display,
		actions: actions == null ? undefined : dialogControls(env, actions),
		errs: errors
    });
    const buildContext = computed<AppContextMap>(() => contextBuilder(root, data, context));
    app.provide(contextKey, buildContext);
    const bus = provideEventBus(app);
    installDirectives(app);
    const form = app.mount(container) as InstanceType<typeof UiRoot>;
    return {
        app,
        data: rd,
        errors,
        bus,
        clearErrors: (path?:string) => {
            if (typeof path === "string") {
                for (let i = errors.length - 1; i >= 0; i--) {
                    if (matchError(errors[i], path, (r) => r)) {
                        errors.splice(i, 1);
                    }
                }
            } else {
                errors.length = 0;
            }
        },
        addError: (error: ApiError) => {
            errors.push(error);
        },
        setDirty: form.setDirty,
        setReadOnly: form.setReadOnly,
        setViewMode: form.setViewMode,
        replaceData: (data:FormData, clearDirty = true):void => {
            Object.assign(rd, data);
            // Data replacement may trigger dirty watcher ... that's why dirty clearance is done in the next tick
            if (clearDirty) nextTick(() => {
                form.setDirty(false);
            });
        },
        buildContext
    };
}

function installSecondaryForm(container:string|Element, env:FormEnvironment, part:FormDefinition, previous:InstalledApp,
							  readonly = false, viewmode = false,
							  display:DialogDisplay = DEFAULT_DIPLAY,
							  actions:FormPartOrReference[] = DEFAULT_ACTIONS):InstalledApp {
    const app = createApp(UiRoot, {
        part: part,
        data: previous.data,
        readonly: readonly,
        viewmode: viewmode,
		display: display,
		actions: actions == null ? undefined : dialogControls(env, actions),
		errs: previous.errors
    });
    app.provide(contextKey, previous.buildContext);
    provideEventBus(app, previous.bus);
    installDirectives(app);
    const form = app.mount(container) as InstanceType<typeof UiRoot>;
    return {
        app,
        data: previous.data,
        errors: previous.errors,
        bus: previous.bus,
        clearErrors: previous.clearErrors,
        addError: previous.addError,
        setDirty: form.setDirty,
        setReadOnly: form.setReadOnly,
        setViewMode: form.setViewMode,
        replaceData: previous.replaceData,
        buildContext: previous.buildContext
    };
}

let dialogIdCounter = 10000;

/**
 * This function will install and display RowObjectDialog to create a new record.
 * Returns the new record data or null if the user canceled the dialog.
 **/
export function installCreateDialog(env:FormEnvironment, modal:boolean, store: boolean, srcType:number, context?:AppContextMap,
									srcSubType?:number, id?: string|number, data?: FormData, button = "create"):Promise<FormData|null> {

	const rd = data == null ? null : (isReactive(data) ? data : reactive<FormData>(data));
	// Create a placeholder for installing the dialog
	const container = document.createElement("div");
	const dialogId = "dialog-container-"+(dialogIdCounter++);
	container.setAttribute("id", dialogId);
	document.body.appendChild(container);
	const app = createApp(RowObjectCreate, {
		srcType: srcType,
		srcSubType: srcSubType,
		modal: modal,
		store: store,
		pk: id,
		initialData: rd,
		button: button,
	});
	const buildContext = computed<AppContextMap>(() => context ?? {});
	app.provide(contextKey, buildContext);
	installDirectives(app);
	const bus = provideEventBus(app);
	app.mount(container);

	let resolve!: (value: FormData | null) => void;
	const promise = new Promise<FormData|null>((res) => {
		resolve = res;
	});

	const subscriptions:number[] = [];

	const cleanUp = () => {
		app.unmount();
		container.remove();
		subscriptions.forEach(s => bus.off(s));
	};

	subscriptions.push(bus.on("closed", () => {
		// Dialog was closed without saving
		cleanUp();
		resolve(null);
	}));

	subscriptions.push(bus.on("created", (data:FormData) => {
		// The new record was saved
		cleanUp();
		resolve(data);
	}));

	return promise;
}


function provideEnv(app: App, env: FormEnvironment, addPreviewSupport = false):App {
    if (addPreviewSupport) app.directive("preview", usePreview());
    return app;
}

export {
    installDirectives,
    installForm,
    installSecondaryForm,
	JsonDump,
    DuplicateParties,
    provideEnv,
    provideEventBus,
    EaType,
    SrcType
};
